import toast from "react-hot-toast";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { enUS } from "@i18n/en_US";
import { createPanelElement } from "@lib/elements";
import {
  DEFAULT_CLEARANCE_CONFIG,
  DEFAULT_DESIGN_COLOR,
  DEFAULT_DESIGN_RELIEF,
  DEFAULT_ELEMENT_MOUNTING_HOLE_CONFIG,
  DEFAULT_MOUNTING_HOLE_CONFIG,
  DEFAULT_PANEL_COLOR,
  DEFAULT_PANEL_OPTIONS,
  PanelElementType,
  normalizePanelModel,
  sanitizePropertiesForType,
  withElementProperties,
  type PanelElement,
  type PanelElementPropertiesMap,
  type PanelModel,
  type Vector2,
} from "@lib/panelTypes";
import { DEFAULT_PANEL_FORMAT } from "@lib/panelFormat";
import { reportDegradation } from "@lib/monitoring";
import { migratePersistedPanelState, PANEL_STATE_VERSION } from "@lib/panelStateMigration";
import { createPanelStateStorage } from "@lib/panelStateStorage";
import { createPanelDimensions } from "@lib/units";
import type { ReferenceImage } from "@lib/referenceImage";

const DEFAULT_PANEL_WIDTH_CM = 10;

type DraftPropertiesState = Partial<{
  [T in PanelElementType]: PanelElementPropertiesMap[T];
}>;

type PanelState = {
  model: PanelModel;
  selectedElementId: string | null;
  selectedElementIds: string[];
  referenceImage: ReferenceImage | null;
  referenceImageSelected: boolean;
  placementType: PanelElementType | null;
  draftProperties: DraftPropertiesState;
};

type ElementsMoveInput = {
  id: string;
  positionMm: Vector2;
};

type PanelActions = {
  setModel: (model: PanelModel) => void;
  setPlacementType: (type: PanelElementType | null) => void;
  setSelectedElement: (id: string | null) => void;
  setSelectedElementIds: (ids: string[]) => void;
  addSelectedElements: (ids: string[]) => void;
  toggleElementSelection: (id: string) => void;
  clearSelection: () => void;
  setDraftProperties: (type: PanelElementType, properties: PanelElement["properties"]) => void;
  addElement: (type: PanelElementType, positionMm: Vector2) => string;
  addPanelElement: (element: PanelElement) => string;
  moveElement: (id: string, positionMm: Vector2) => void;
  moveElements: (updates: ElementsMoveInput[]) => void;
  updateElement: (id: string, updater: (el: PanelElement) => PanelElement) => void;
  removeElement: (id: string) => void;
  removeElements: (ids: string[]) => void;
  setReferenceImage: (image: ReferenceImage | null) => void;
  updateReferenceImage: (updates: Partial<ReferenceImage>) => void;
  selectReferenceImage: (selected: boolean) => void;
  reset: () => void;
};

const createInitialModel = (): PanelModel =>
  normalizePanelModel({
    dimensions: createPanelDimensions(DEFAULT_PANEL_WIDTH_CM),
    elements: [],
    options: { ...DEFAULT_PANEL_OPTIONS },
    mountingHoleConfig: { ...DEFAULT_MOUNTING_HOLE_CONFIG },
    elementHoleConfig: { ...DEFAULT_ELEMENT_MOUNTING_HOLE_CONFIG },
    clearance: { ...DEFAULT_CLEARANCE_CONFIG },
    panelColor: DEFAULT_PANEL_COLOR,
    designColor: DEFAULT_DESIGN_COLOR,
    designRelief: { ...DEFAULT_DESIGN_RELIEF },
    format: { ...DEFAULT_PANEL_FORMAT },
  });

export const usePanelStore = create<PanelState & PanelActions>()(
  persist(
    (set, get) => ({
      model: createInitialModel(),
      selectedElementId: null,
      selectedElementIds: [],
      referenceImage: null,
      referenceImageSelected: false,
      placementType: null,
      draftProperties: {},
      setModel: (model) => set({ model: normalizePanelModel(model) }),
      setPlacementType: (type) => set({ placementType: type }),
      setSelectedElement: (id) =>
        set(() => ({
          selectedElementId: id,
          selectedElementIds: id ? [id] : [],
          referenceImageSelected: false,
        })),
      setSelectedElementIds: (ids) =>
        set(() => {
          const unique = Array.from(new Set(ids.filter(Boolean)));
          return {
            selectedElementIds: unique,
            selectedElementId: unique.length ? unique[unique.length - 1] : null,
            referenceImageSelected: false,
          };
        }),
      addSelectedElements: (ids) =>
        set((state) => {
          const existing = new Set(state.selectedElementIds);
          const nextIds = [...state.selectedElementIds];
          let lastAdded: string | null = null;
          ids.forEach((id) => {
            if (!id || existing.has(id)) {
              return;
            }
            existing.add(id);
            nextIds.push(id);
            lastAdded = id;
          });
          return {
            selectedElementIds: nextIds,
            selectedElementId: lastAdded ?? state.selectedElementId,
            referenceImageSelected: false,
          };
        }),
      toggleElementSelection: (id) =>
        set((state) => {
          if (!id) {
            return state;
          }
          const exists = state.selectedElementIds.includes(id);
          if (!exists) {
            return {
              selectedElementIds: [...state.selectedElementIds, id],
              selectedElementId: id,
              referenceImageSelected: false,
            };
          }
          const nextIds = state.selectedElementIds.filter((value) => value !== id);
          return {
            selectedElementIds: nextIds,
            selectedElementId: nextIds.length ? nextIds[nextIds.length - 1] : null,
            referenceImageSelected: false,
          };
        }),
      clearSelection: () =>
        set({ selectedElementId: null, selectedElementIds: [], referenceImageSelected: false }),
      setDraftProperties: (type, properties) =>
        set((state) => {
          const sanitized = sanitizePropertiesForType(type, properties);
          if (!sanitized) {
            const nextDraft = { ...state.draftProperties };
            delete nextDraft[type];
            return { draftProperties: nextDraft };
          }
          return {
            draftProperties: { ...state.draftProperties, [type]: sanitized },
          };
        }),
      addElement: (type, positionMm) => {
        const element = withElementProperties(
          createPanelElement(type, positionMm, get().model.format.system),
          get().draftProperties[type],
        );
        set((state) => ({
          model: {
            ...state.model,
            elements: [...state.model.elements, element],
          },
          selectedElementId: element.id,
          selectedElementIds: [element.id],
        }));
        return element.id;
      },
      addPanelElement: (element) => {
        set((state) => ({
          model: {
            ...state.model,
            elements: [...state.model.elements, element],
          },
          selectedElementId: element.id,
          selectedElementIds: [element.id],
          referenceImageSelected: false,
        }));
        return element.id;
      },
      moveElement: (id, positionMm) =>
        set((state) => ({
          model: {
            ...state.model,
            elements: state.model.elements.map((element) =>
              element.id === id ? { ...element, positionMm } : element,
            ),
          },
        })),
      moveElements: (updates) =>
        set((state) => {
          if (!updates.length) {
            return state;
          }
          const updateMap = new Map(updates.map((entry) => [entry.id, entry.positionMm]));
          return {
            model: {
              ...state.model,
              elements: state.model.elements.map((element) => {
                const nextPosition = updateMap.get(element.id);
                return nextPosition ? { ...element, positionMm: nextPosition } : element;
              }),
            },
          };
        }),
      updateElement: (id, updater) =>
        set((state) => ({
          model: {
            ...state.model,
            elements: state.model.elements.map((element) =>
              element.id === id ? updater(element) : element,
            ),
          },
        })),
      removeElement: (id) =>
        set((state) => ({
          model: {
            ...state.model,
            elements: state.model.elements.filter((element) => element.id !== id),
          },
          selectedElementIds: state.selectedElementIds.filter((elementId) => elementId !== id),
          selectedElementId: state.selectedElementId === id ? null : state.selectedElementId,
        })),
      removeElements: (ids) =>
        set((state) => {
          const removeSet = new Set(ids);
          if (!removeSet.size) {
            return state;
          }
          const nextElements = state.model.elements.filter((element) => !removeSet.has(element.id));
          const nextSelection = state.selectedElementIds.filter((id) => !removeSet.has(id));
          return {
            model: {
              ...state.model,
              elements: nextElements,
            },
            selectedElementIds: nextSelection,
            selectedElementId: nextSelection.length
              ? nextSelection[nextSelection.length - 1]
              : null,
          };
        }),
      setReferenceImage: (image) =>
        set(() => ({
          referenceImage: image,
          referenceImageSelected: Boolean(image),
          selectedElementId: null,
          selectedElementIds: [],
        })),
      updateReferenceImage: (updates) =>
        set((state) => {
          if (!state.referenceImage) {
            return state;
          }
          return {
            referenceImage: { ...state.referenceImage, ...updates },
          };
        }),
      selectReferenceImage: (selected) =>
        set(() => ({
          referenceImageSelected: selected,
          selectedElementId: selected ? null : null,
          selectedElementIds: selected ? [] : [],
        })),
      reset: () =>
        set({
          model: createInitialModel(),
          selectedElementId: null,
          selectedElementIds: [],
          referenceImage: null,
          referenceImageSelected: false,
          placementType: null,
        }),
    }),
    {
      name: "panel-designer-store",
      storage: createPanelStateStorage<(PanelState & PanelActions) | undefined>({
        onReferenceImageDropped: (error, imageChars) => {
          toast(enUS.referenceImage.notKept, { id: "reference-image-not-kept" });
          reportDegradation(error, "autosave", "reference-image-dropped", { imageChars });
        },
        onSaveFailed: (error) => {
          toast.error(enUS.app.storageFull, { id: "autosave-failed" });
          reportDegradation(error, "autosave", "save-failed");
        },
      }),
      // Migrating re-normalizes the persisted model: see `PANEL_STATE_VERSION` for the history.
      version: PANEL_STATE_VERSION,
      migrate: (state, version) =>
        migratePersistedPanelState(
          state as (PanelState & PanelActions) | undefined,
          version,
          createInitialModel,
        ),
    },
  ),
);
